import {
  Alert,
  Box,
  ButtonGroup,
  chakra,
  Container,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Switch,
  Text,
} from "@chakra-ui/react";
//import type { Value,  } from '@react-page/editor';
//import Editor from '@react-page/editor';
import { yupResolver } from "@hookform/resolvers/yup";
import ButtonEmailPreview from "components/ButtonEmailPreview";
import ButtonBack from "components/ui/ButtonBack";
import ButtonBackToList from "components/ui/ButtonBackToList";
import CardForm from "components/ui/CardForm";
import FixedBar from "components/ui/FixedBar";
import FormControlGroup from "components/ui/FormControlGroup";
import {
  ResourceCreatableSelect,
  ResourceSelect,
  SelectOption,
} from "components/ui/ResourceCreatableSelect";
import SaveButton from "components/ui/SaveButton";
import SubmitButton from "components/ui/SubmitButton";
import WrapperDialog from "components/ui/WrapperDialog";
import { format } from "date-fns";
import UploadAsset from "features/companies/components/UploadAsset";
import { useContentGroup } from "features/content-groups/hooks/useContentGroup";
import { BlockEditor } from "features/editor/components/BlockEditor";
import SimpleEditor from "features/editor/components/SimpleEditor/SimpleEditor";
import StepperList from "features/editor/extensions/Stepper/StepperList";
import { useMessages } from "hooks/useMessages";
import { useResource } from "hooks/useResource";
import { preventEnterKeySubmission } from "lib/validation";
import { useState } from "react";
import {
  FormProvider,
  Resolver,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { LinkContentPreview, wrapperDialogDefaultProps } from "../config";
import { useSaveContent } from "../hooks/useSaveContent";

const HelperText = chakra("small", {
  baseStyle: {
    pos: "absolute",
    w: "full",
    left: 0,
    bottom: "-23px",
  },
});

export interface FormValues {
  contentGroupId: string;
  title: string;
  subject: string;
  originalUrl: string;
  originalUrlButtonText: string;
  summary: string;
  content: any;
  guidelines: string;
  image: string;
  attachment: any;
  attachmentButtonText: string;
  //subcategoryId: string;
  subcategory: SelectOption;
  theme: SelectOption;
  legislativeProposal: SelectOption;
  tags: [];
  authors: [];
  rapporteurs: [];
  onlySave?: boolean;
}

const validationSchema = Yup.object().shape({
  contentGroupId: Yup.string().required(),
  title: Yup.string().required(),
  subject: Yup.string().required(),
  summary: Yup.string().required(),
  //subcategoryId: Yup.string().required(),
  subcategory: Yup.object().required(),
  theme: Yup.object().required(),
  //content: Yup.mixed().required(),
});

export default function CreateContent() {
  const { id, content_id } = useParams();
  const { data: content } = useResource({
    id: content_id || "",
    resourceName: "content",
    path: "contents",
  });
  const { data: contentGroup } = useContentGroup(id);

  const isReady = (!content_id && contentGroup) || (content_id && content);

  if (isReady) {
    return (
      <FormContent
        content={content}
        contentGroupId={id}
        contentGroup={contentGroup}
        guidelines={contentGroup.emailTemplate.guidelines}
      />
    );
  }
  return null;
}

export function FormContent({
  isEditing,
  content,
  contentGroupId,
  contentGroup,
  guidelines,
}: any) {
  const isSent = !!(content && content.email);

  if (!contentGroupId) contentGroupId = content?.contentGroupId;
  if (!contentGroup) contentGroup = content?.contentGroup;

  const [isLegislativeProposal, setIsLegislativeProposal] = useState<boolean>(
    !!content?.legislativeProposal
  );
  const navigate = useNavigate();

  //const { data: categories } = useResourceList({ resourceName: 'categories', path: '/template-categories', params: { contentGroupId } });

  //const { id } = useParams();

  //const [value, setValue] = useState<Value | null>(null);
  const defaultValues = {
    contentGroupId: contentGroup.id,
    title: content?.title,
    subject: content?.subject,
    originalUrl: content?.originalUrl,
    originalUrlButtonText: content?.originalUrlButtonText,
    summary: content?.summary,
    //subcategoryId: content?.subcategoryId,
    subcategory: content?.subcategory
      ? {
          value: content.subcategory.id,
          label: `${content.category.name} - ${content.subcategory.name}`,
        }
      : null,
    guidelines: guidelines ?? content?.guidelines,
    image: content?.image,
    attachment: content?.attachment,
    attachmentButtonText: content?.attachmentButtonText,
    content: content?.content ? JSON.parse(content.content) : null,
    theme: content?.theme
      ? {
          value: content.theme.id,
          label: content.theme.name,
        }
      : null,

    legislativeProposal: content?.legislativeProposal
      ? {
          value: content.legislativeProposal.id,
          label: content.legislativeProposal.name,
        }
      : null,

    tags: content?.tags
      ? content.tags.map((tag) => ({
          value: tag.id,
          label: tag.name,
          colorScheme: tag.colorScheme,
        }))
      : null,

    authors: content?.authors
      ? content.authors.map((author) => ({
          value: author.id,
          label: author.name,
        }))
      : null,

    rapporteurs: content?.rapporteurs
      ? content.rapporteurs.map((rapporteur) => ({
          value: rapporteur.id,
          label: rapporteur.name,
        }))
      : null,
  };

  const methods = useForm<FormValues>({
    mode: "all",
    resolver: yupResolver(validationSchema) as unknown as Resolver<FormValues>,
    defaultValues,
  });

  const {
    register,
    control,
    setValue,
    handleSubmit,
    formState: { errors, isValid },
  } = methods;

  const { onSuccess } = useMessages({
    success: "Fique tranquilo, seu trabalho foi salvo",
  });

  const contentId = content?.id;

  const { onSaveContent, isPending, variables } = useSaveContent({
    language: contentGroup.language,
    contentId: isEditing ? contentId : null,
    onSuccess: (content, variables) => {
      if (variables.onlySave) {
        onSuccess();
        if (!contentId) {
          navigate(`/disparos-de-report/editar/${content.id}/conteudo`);
        }
      } else navigate(`/disparos-de-report/editar/${content.id}/listas`);
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async ({
    theme,
    subcategory,
    legislativeProposal,
    ...data
  }) => {
    await onSaveContent({
      ...data,
      themeId: theme?.value ?? undefined,
      subcategoryId: subcategory?.value ?? undefined,
      legislativeProposalId: legislativeProposal?.value ?? undefined,
    });
  };

  const onSave: SubmitHandler<FormValues> = async (data) => {
    await onSubmit({ ...data, onlySave: true });

    if (isSent) {
      navigate('/disparos-de-report');
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        onKeyPress={preventEnterKeySubmission}
      >
        <Container variant="internal" flexDirection="column" mb="80px">
          <Flex justifyContent="space-between">
            <Heading as="h1" size="h1">
              Conteúdo
              <Text as="span">
                {format(new Date(), "EEEE, dd 'de' LLLL 'de' yyyy")}
              </Text>
            </Heading>

            <ButtonGroup>
              {content && (
                <ButtonEmailPreview url={LinkContentPreview(content)} />
              )}
              <ButtonBackToList to={`/disparos-de-report`} />
            </ButtonGroup>
          </Flex>

          <CardForm
            boxShadow="none"
            border="1px solid #E2E2E2"
            bgColor="rgba(240, 241, 242, 0.60)"
          >
            <Heading as="h3" size="h3">
              Parâmetros
            </Heading>
            <FormControlGroup mt="30px">
              <FormControl isInvalid={!!errors.subcategory} isRequired>
                <FormLabel>Poder/Órgão</FormLabel>
                <ResourceSelect
                  control={control}
                  name="subcategory"
                  resourceKey="categories"
                  listPath="/template-categories/subcategories-list"
                  listParams={{ contentGroupId }}
                  placeholder="Selecione uma opção"
                />
              </FormControl>

              <FormControl isInvalid={!!errors.theme} isRequired>
                <FormLabel>Tema</FormLabel>
                <ResourceSelect
                  control={control}
                  name="theme"
                  resourceKey="contentParameters"
                  listPath="/content-parameters"
                  listParams={{
                    type: "THEME",
                    language: contentGroup.language,
                    active: true,
                  }}
                  placeholder="Selecione uma opção"
                />
              </FormControl>
            </FormControlGroup>

            <FormControlGroup>
              <FormControl display="flex" alignItems="center" gap="10px">
                <Switch
                  id="legislativeProposalFlag"
                  isChecked={isLegislativeProposal}
                  onChange={(e) => {
                    e.preventDefault();
                    setIsLegislativeProposal((state) => {
                      const newState = !state;
                      if (!newState) {
                        setValue("legislativeProposal", null);
                        setValue("authors", []);
                        setValue("rapporteurs", []);
                      }
                      return newState;
                    });
                  }}
                />
                <FormLabel htmlFor="legislativeProposalFlag" mb="0">
                  Proposição Legislativa
                </FormLabel>
              </FormControl>
            </FormControlGroup>

            <FormControlGroup display={isLegislativeProposal ? "flex" : "none"}>
              <FormControl>
                <FormLabel>Proposição Legislativa</FormLabel>
                <ResourceCreatableSelect
                  control={control}
                  name="legislativeProposal"
                  resourceKey="pls"
                  listPath="/pls"
                  createPath="/pls"
                  placeholder="Digite para buscar ou criar PLs"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Autor</FormLabel>
                <ResourceCreatableSelect
                  control={control}
                  name="authors"
                  resourceKey="people"
                  listPath="/people"
                  createPath="/people"
                  isMulti
                  placeholder="Digite para buscar ou criar Autores"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Relator</FormLabel>
                <ResourceCreatableSelect
                  control={control}
                  name="rapporteurs"
                  resourceKey="people"
                  listPath="/people"
                  createPath="/people"
                  isMulti
                  placeholder="Digite para buscar ou criar Relatores"
                />
              </FormControl>
            </FormControlGroup>
          </CardForm>

          <CardForm>
            <Heading as="h3" size="h3">
              Conteúdo
            </Heading>
            <FormControlGroup mt="30px">
              <FormControl isInvalid={!!errors.title} isRequired>
                <FormLabel>Título</FormLabel>
                <Input {...register("title")} maxLength={160} />
              </FormControl>
              <FormControl isInvalid={!!errors.subject} isRequired>
                <FormLabel>Assunto do e-mail</FormLabel>
                <Input {...register("subject")} maxLength={100} />
              </FormControl>
            </FormControlGroup>

            <FormControlGroup>
              <FormControl>
                <FormLabel>Imagem lateral (exibida nas newsletters)</FormLabel>
                <UploadAsset
                  defaultValue={defaultValues.image}
                  onChange={(url) =>
                    setValue("image", url, { shouldValidate: true })
                  }
                />
              </FormControl>
              <FormControl h="full" isInvalid={!!errors.summary} isRequired>
                <FormLabel>Por que isso importa?</FormLabel>
                <SimpleEditor name="summary" control={control} />
              </FormControl>
            </FormControlGroup>

            <Divider my="25px" />

            <FormControlGroup>
              <FormControl isInvalid={!!errors.originalUrl}>
                <FormLabel>Link do original</FormLabel>
                <Input {...register("originalUrl")} />
              </FormControl>
              <FormControl
                isInvalid={!!errors.originalUrlButtonText}
                alignSelf="flex-end"
                pos="relative"
              >
                <Input
                  {...register("originalUrlButtonText")}
                  placeholder="Texto do botão"
                />
                <HelperText>
                  Caso não seja informado, será utilizado o texto: "Fonte"
                </HelperText>
              </FormControl>
            </FormControlGroup>

            <FormControlGroup>
              <FormControl>
                <FormLabel>Arquivo para download</FormLabel>
                <UploadAsset
                  defaultValue={defaultValues.attachment}
                  onChange={(attachment) =>
                    setValue("attachment", attachment, { shouldValidate: true })
                  }
                  accept={{
                    "application/pdf": [],
                  }}
                  description="PDF até 5MB"
                  asFile
                >
                  <FormControl mb="35px">
                    <Input
                      {...register("attachmentButtonText")}
                      placeholder="Texto do botão"
                    />
                    <HelperText>
                      Caso não seja informado, será utilizado o texto:
                      "Download"
                    </HelperText>
                  </FormControl>
                </UploadAsset>
              </FormControl>
            </FormControlGroup>

            <FormControl>
              <FormLabel>Conteúdo</FormLabel>
              <Box bgColor="rgba(240, 241, 242, 0.60)" py="48px">
                <Box maxW="720px" mx="auto" mb="30px">
                  <strong>
                    Use esses passos para guiar a criação do seu conteúdo
                  </strong>
                  <Alert status="warning" variant="left-accent" mt="5px">
                    <SimpleEditor name="guidelines" control={control} noCSS />
                  </Alert>
                </Box>

                <Box maxW="720px" mx="auto" mb="5px">
                  <strong>Conteúdo do seu e-mail</strong>
                </Box>
                <BlockEditor
                  name="content"
                  control={control}
                  customExtensions={[
                    StepperList.configure({
                      language: contentGroup.language,
                    }),
                  ]}
                />
              </Box>
            </FormControl>
          </CardForm>
        </Container>

        <FixedBar>
          <WrapperDialog {...wrapperDialogDefaultProps}>
            <ButtonBack
              as={Link}
              to={
                content
                  ? "/disparos-de-report"
                  : `/disparos-de-report/novo/${contentGroupId}`
              }
            />
          </WrapperDialog>

          <ButtonGroup>
            <SaveButton
              isDisabled={!isValid}
              isLoading={variables?.onlySave && isPending}
              onClick={handleSubmit(onSave)}
            >
              Salvar
            </SaveButton>

            {!isSent && (
              <SubmitButton
                isDisabled={!isValid}
                isLoading={!variables?.onlySave && isPending}
              >
                Salvar e continuar
              </SubmitButton>
            )}
          </ButtonGroup>
        </FixedBar>
      </form>
    </FormProvider>
  );
}
